/* eslint-disable @stencil-community/own-props-must-be-private */
/* STENCIL IMPORTS */
import {
  Component,
  Element,
  getAssetPath,
  Host,
  h,
  Prop,
  State,
  Watch
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import {
  ChComboBoxRenderCustomEvent,
  ComboBoxModel,
  TreeViewItemModel,
  TreeViewItemModelExtended
} from "@genexus/chameleon-controls-library";
/* CUSTOM IMPORTS */
import { Locale } from "../../common/locale";
import {
  convertReferenceDataToTreeViewData,
  mapSelectorCategoryDataToComboBoxModel
} from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/pills",
  "components/combo-box",
  "components/icon",
  "components/checkbox",
  "components/tree-view",
  "utils/spacing",
  "utils/layout",
  "utils/form",
  "utils/typography"
];

const GEMINI_TOOLS_SHOW_MORE_HORIZONTAL = getIconPath({
  category: "gemini-tools",
  name: "show-more-horizontal",
  colorType: "primary"
});
const NAVIGATION_ARROW_DOWN_SKY_BLUE = getIconPath({
  category: "navigation",
  name: "arrow-down-skyblue",
  colorType: "primary"
});

const SUGGEST_OPTIONS = {
  alreadyProcessed: true,
  autoExpand: false,
  hideMatchesAndShowNonMatches: false,
  highlightMatchedItems: false,
  matchCase: false,
  regularExpression: false,
  strict: false,
  renderActiveItemIconOnExpand: true
};

@Component({
  tag: "gx-ide-references",
  styleUrl: "references.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/references"]
})
export class GxIdeReferences {
  #objectDataMap: objectDataMap = new Map();
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/references/shortcuts.json`);
  #_componentLocale: any;

  @Element() el: HTMLGxIdeReferencesElement;
  #selectObjectSuggestEl!: HTMLChComboBoxRenderElement;

  /**
   * Displays or hides the header-bottom section
   */
  @State() barHidden = false;

  /**
   * Objects suggestions that will appear on the suggest
   */
  @State() objectsSuggestions: ComboBoxModel;

  @State() referencedByTreeData: TreeViewItemModel[];

  @State() referencesToTreeData: TreeViewItemModel[];

  /**
   * Callback invoked when a node is expanded in any references panels
   */
  @Prop() readonly loadReferencesCallback?: LoadReferencesCallback;

  /**
   * Callback invoked when user wants to open an object
   */
  @Prop() readonly openObjectCallback?: OpenObjectCallback;

  /**
   * Callback invoked when user wants to show object selection dialog
   */
  @Prop() readonly openSelectorDialogCallback?: OpenSelectorDialogCallback;

  /**
   * Callback invoked when any item from the references panels is selected
   */
  @Prop() readonly selectReferenceCallback?: SelectReferenceCallback;

  /**
   * Callback invoked when user writes on object selector input
   */
  @Prop() readonly selectorSourceCallback?: SelectorSourceCallback;

  /**
   * Currently selected object
   */
  @Prop({ mutable: true }) selectedObject?: ObjectData;

  @Watch("selectedObject")
  selectedObjectChanged(newSelectedObject: ObjectData) {
    if (newSelectedObject?.id) {
      /* update "is referenced by*/
      this.#loadReferencesHandler(newSelectedObject.id, false);
      /* update "has references to*/
      this.#loadReferencesHandler(newSelectedObject.id, true);
    }
  }

  componentDidLoad() {
    this.#selectObjectSuggestEl.focus();
  }

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.#evaluateInitialReferenceData();
  }

  /**
   * Evaluates whether to call loadReferencesCallback based on the presence of a selected object.
   */
  #evaluateInitialReferenceData = () => {
    if (this.selectedObject) {
      this.selectedObjectChanged(this.selectedObject);
    }
  };

  /**
   * Hides or shows the bar (the bottom section of the header).
   */
  #hideBarButtonClickedHandler = () => {
    this.barHidden = !this.barHidden;
  };

  /**
   * Invokes 'loadReferencesCallback' for 'Is Referenced By' and 'Has References To' panels.
   */
  #loadReferencesHandler = async (id: string, to = false) => {
    if (this.loadReferencesCallback) {
      this.loadReferencesCallback(id, to).then(result => {
        if (to) {
          this.referencesToTreeData =
            convertReferenceDataToTreeViewData(result);
        } else {
          this.referencedByTreeData =
            convertReferenceDataToTreeViewData(result);
        }
      });
    }
  };

  #openObjectCallbackHandler = () => {
    // Returns an empty Promise
    this.openObjectCallback(this.selectedObject?.id);
  };

  /**
   * Fired when user clicks the ch-suggest button (...)
   */
  #openSelectorDialogCallbackHandler = async () => {
    const objectDataResult = await this.openSelectorDialogCallback();
    if (objectDataResult) {
      this.selectedObject = objectDataResult;
    }
  };

  #referenceSelectedHandler = (
    event: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    if (this.selectReferenceCallback) {
      const selectedItems: string[] = event.detail.map(item => item.item.id);
      this.selectReferenceCallback(selectedItems[0]);
      // returns void
    }
  };

  #referencesItemOpenReferenceCallback = (
    itemOpened: CustomEvent<{ id: string; leaf: boolean; metadata: string }>
  ) => {
    const itemId = itemOpened.detail.id;
    if (this.openObjectCallback) {
      this.openObjectCallback(itemId);
      // returns void
    }
  };

  #referencesLazyLoadCallbackHandler =
    (ref: "references-to" | "referenced-by") =>
    async (treeItemId: string): Promise<TreeViewItemModel[]> => {
      if (this.loadReferencesCallback) {
        const to = ref === "references-to";
        const referencesLazyChildren = await this.loadReferencesCallback(
          treeItemId,
          to
        );
        const treeLazyChildren = convertReferenceDataToTreeViewData(
          referencesLazyChildren
        );
        return new Promise(resolve => {
          resolve(treeLazyChildren);
        });
      }
      return new Promise(resolve => {
        resolve([]);
      });
    };

  #selectObjectChangedHandler = (
    event: ChComboBoxRenderCustomEvent<string> | InputEvent
  ) => {
    const newSelectedValue = event.detail as string;

    const objectData = this.#objectDataMap.get(newSelectedValue);
    if (objectData) {
      this.selectedObject = objectData;
    }
  };

  #selectObjectInputHandler = (
    event: ChComboBoxRenderCustomEvent<string> | InputEvent
  ) => {
    // WA to know if the event was from searching on the input or an item selection.
    // const partValue = (e.target as HTMLElement).getAttribute("part");
    const typedValue = event.detail as string;
    if (this.selectorSourceCallback) {
      this.selectorSourceCallback(typedValue).then(
        selectorCategoryDataArray => {
          // update map
          this.#objectDataMap.clear();
          selectorCategoryDataArray?.forEach(selectorCategoryData => {
            selectorCategoryData.items.forEach(objectData => {
              this.#objectDataMap.set(objectData.description, {
                id: objectData.id,
                typeName: objectData.typeName,
                icon: objectData.icon,
                name: objectData.name,
                description: objectData.description
              });
            });
          });

          this.objectsSuggestions = mapSelectorCategoryDataToComboBoxModel(
            selectorCategoryDataArray
          );
        }
      );
    }
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <section class="section">
          <header class="header dialog-header-with-border spacing-body-inline spacing-body-block-start">
            <div class="header__top field-group">
              <button
                type="button"
                class="button-tertiary button-icon-only"
                aria-label={this.#_componentLocale.header.toggleBar}
                part="toggle-bar-button"
                onClick={this.#hideBarButtonClickedHandler}
              >
                <ch-image
                  class="icon-m"
                  src={NAVIGATION_ARROW_DOWN_SKY_BLUE}
                  containerRef={this.el}
                ></ch-image>
              </button>

              <div class="field field-inline">
                <label
                  // select object
                  class="label"
                  part="select-object-label"
                  htmlFor="object-selector-suggest"
                >
                  {this.#_componentLocale.header.selectObject}
                </label>
                <ch-combo-box-render
                  accessibleName={this.#_componentLocale.header.selectObject}
                  class="combo-box"
                  model={this.objectsSuggestions}
                  suggest
                  onInput={this.#selectObjectInputHandler}
                  onChange={this.#selectObjectChangedHandler}
                  value={this.selectedObject?.name}
                  part="object-selector-suggest"
                  suggestOptions={SUGGEST_OPTIONS}
                  ref={(el: HTMLChComboBoxRenderElement) =>
                    (this.#selectObjectSuggestEl =
                      el as HTMLChComboBoxRenderElement)
                  }
                ></ch-combo-box-render>
              </div>

              <button
                type="button"
                class="button-tertiary button-icon-only"
                aria-label={this.#_componentLocale.header.openSelectorButton}
                onClick={this.#openSelectorDialogCallbackHandler}
                part="open-selector-dialog-button"
              >
                <ch-image
                  class="icon-m"
                  src={GEMINI_TOOLS_SHOW_MORE_HORIZONTAL}
                  containerRef={this.el}
                ></ch-image>
              </button>
            </div>

            <div
              class={{
                "header__bottom": true,
                "header__bottom--hidden": this.barHidden
              }}
            >
              <div class="outer-wrapper">
                <div class="inner-wrapper">
                  <div class="inner-wrapper__left">
                    <ch-image
                      class="icon-m"
                      src={this.selectedObject.icon}
                      containerRef={this.el}
                    ></ch-image>
                    <a
                      class="text-link"
                      onClick={this.#openObjectCallbackHandler}
                      part="open-object-text-link"
                      aria-label={
                        this.#_componentLocale.main.selectedObjectLink
                      }
                    >
                      {`${this.selectedObject?.name} : ${this.selectedObject?.description}`}
                    </a>
                  </div>
                  {/* <div class="inner-wrapper__right">
                    TODO: Add this feature "Group References by Type".
                    <ch-checkbox
                      class="checkbox"
                      id="references-by-type"
                      part="references-type-checkbox"
                      checkedValue="referencesByType"
                      caption="Group references by type"
                    ></ch-checkbox>
                  </div> */}
                </div>
              </div>
            </div>
          </header>
          <div class="main">
            <section class="section__referenced-by">
              <h2 class="dialog-header dialog-header-with-border dialog-header__referenced-by heading-5 text-align-center spacing-body-inline subtitle-regular-xs">
                {this.#_componentLocale.main.isReferencedBy}
              </h2>

              <div
                // is referenced by
                class="main__referenced-by spacing-body-block-start spacing-body-block-end"
              >
                <ch-tree-view-render
                  class={{
                    "gxi-hidden": !this.referencedByTreeData?.length,
                    "tree-view": true
                  }}
                  part="referenced-by-tree"
                  model={this.referencedByTreeData}
                  aria-label={
                    this.#_componentLocale.main.referencedByTreeAccessible
                  }
                  lazyLoadTreeItemsCallback={this.#referencesLazyLoadCallbackHandler(
                    "referenced-by"
                  )}
                  onSelectedItemsChange={this.#referenceSelectedHandler}
                  dragDisabled={true}
                  dropDisabled={true}
                  onItemOpenReference={
                    this.#referencesItemOpenReferenceCallback
                  }
                  showLines="last"
                ></ch-tree-view-render>
                {!this.referencedByTreeData?.length && (
                  <gx-ide-empty-state
                    stateTitle={
                      this.referencedByTreeData?.length === 0
                        ? this.#_componentLocale.main.isReferencedByEmpty
                        : this.#_componentLocale.main.isReferencedByError
                    }
                  ></gx-ide-empty-state>
                )}
              </div>
            </section>

            <section class="section__references-to">
              <h2 class="dialog-header dialog-header-with-border dialog-header__references-to heading-5 text-align-center spacing-body-inline subtitle-regular-xs">
                {this.#_componentLocale.main.hasReferencesTo}
              </h2>

              <div
                // has references to
                class="main__references-to spacing-body-block-start spacing-body-block-end"
              >
                <ch-tree-view-render
                  class={{
                    "gxi-hidden": !this.referencesToTreeData?.length,
                    "tree-view": true
                  }}
                  part="referenced-to-tree"
                  model={this.referencesToTreeData}
                  aria-label={
                    this.#_componentLocale.main.referencesToTreeAccessible
                  }
                  lazyLoadTreeItemsCallback={this.#referencesLazyLoadCallbackHandler(
                    "references-to"
                  )}
                  onSelectedItemsChange={this.#referenceSelectedHandler}
                  dragDisabled={true}
                  dropDisabled={true}
                  onItemOpenReference={
                    this.#referencesItemOpenReferenceCallback
                  }
                  showLines="last"
                ></ch-tree-view-render>
                {!this.referencesToTreeData?.length && (
                  <gx-ide-empty-state
                    stateTitle={
                      this.referencesToTreeData?.length === 0
                        ? this.#_componentLocale.main.hasReferencesEmpty
                        : this.#_componentLocale.main.hasReferencesToError
                    }
                  ></gx-ide-empty-state>
                )}
              </div>
            </section>
          </div>
        </section>

        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
      </Host>
    );
  }
}

export type LoadReferencesCallback = (
  id: string,
  to: boolean
) => Promise<ReferenceData[]>;

export type ObjectData = {
  id: string;
  typeName: string;
  icon: string;
  name: string;
  description?: string;
};

export type OpenObjectCallback = (id: string) => Promise<void>;

export type OpenSelectorDialogCallback = () => Promise<ObjectData | undefined>;

export type ReferenceData = ObjectData & {
  hasChildren: boolean;
  items?: ReferenceData[];
};

export type SelectReferenceCallback = (id: string) => Promise<void>;

export type SelectorCategoryData = {
  name: string;
  items: ObjectData[];
};

export type SelectorSourceCallback = (
  prefix: string
) => Promise<SelectorCategoryData[]>;

export type objectDataMap = Map<string, ObjectData>;
